import React from 'react';
import { PageHeader } from '../../banners/page-header/page-header';
import { Section } from '../../../core/layout/section/section';
import { useId } from '../../../utils/hooks';

type ErrorPageActionVariant = 'primary' | 'secondary';

type ErrorPageAction = {
    label: string;
    variant?: ErrorPageActionVariant;
    href?: string;
    onClick?: React.MouseEventHandler<HTMLButtonElement | HTMLAnchorElement>;
};

type CreateDefaultErrorActionsOptions = {
    backLabel: string;
    contactLabel: string;
    contactHref: string;
    fallbackHref?: string;
    windowRef?: Pick<Window, 'history' | 'location'>;
};

type ErrorPageDefaultActions = {
    primaryAction: ErrorPageAction;
    secondaryAction: ErrorPageAction;
};

const PRIMARY_VARIANT: ErrorPageActionVariant = 'primary';
const SECONDARY_VARIANT: ErrorPageActionVariant = 'secondary';

const actionClassName = (variant: ErrorPageActionVariant = PRIMARY_VARIANT) => `flos-button flos-button--${variant}`;

const renderAction = (action: ErrorPageAction, fallbackVariant: ErrorPageActionVariant = PRIMARY_VARIANT) => {
    const variant = action.variant ?? fallbackVariant;

    if (action.href) {
        return (
            <a className={actionClassName(variant)} href={action.href} onClick={action.onClick}>
                {action.label}
            </a>
        );
    }

    return (
        <button type="button" className={actionClassName(variant)} onClick={action.onClick}>
            {action.label}
        </button>
    );
};

const createDefaultErrorActions = ({ backLabel, contactLabel, contactHref, fallbackHref, windowRef }: CreateDefaultErrorActionsOptions): ErrorPageDefaultActions => {
    const safeWindow = windowRef ?? (typeof window !== 'undefined' ? window : undefined);
    const resolvedFallbackHref = fallbackHref ?? safeWindow?.location?.href ?? '.';

    const handleBack: React.MouseEventHandler<HTMLButtonElement | HTMLAnchorElement> = (event) => {
        event.preventDefault();

        if (!safeWindow) {
            return;
        }

        const { history, location } = safeWindow;

        if (history && typeof history.back === 'function' && history.length > 1) {
            history.back();
            return;
        }

        if (location && typeof location.assign === 'function') {
            location.assign(resolvedFallbackHref);
        }
    };

    return {
        primaryAction: {
            label: backLabel,
            variant: PRIMARY_VARIANT,
            onClick: handleBack,
        },
        secondaryAction: {
            label: contactLabel,
            variant: SECONDARY_VARIANT,
            href: contactHref,
        },
    };
};

type ErrorPageProps = {
    errorHeader?: string;
    errorTitle?: string;
    errorDescription?: string;
    errorBackText?: string;
    errorContactLink?: string;
    errorContactText?: string;
    primaryAction?: ErrorPageAction;
    secondaryAction?: ErrorPageAction | null;
} & React.ComponentPropsWithoutRef<'div'>;

/**
 * ErrorPage is a common component in most Hybrid App when there is a fatal error that the App could not recover easily.
 * All the keys of resources are required; please provide a default value for them instead of relying on resource bundle because ajax to get resource bundle may fail
 */
const ErrorPage: React.FC<ErrorPageProps> = ({
    errorHeader = 'Der opstod en fejl',
    errorTitle = 'Der opstod en fejl',
    errorDescription = 'Du kan prøve igen eller kontakte os hvis problemet forsætter.',
    errorBackText = 'Tilbage',
    errorContactLink = 'https://www.topdanmark.dk/faa-hjaelp/kontakt-os/',
    errorContactText = 'Kontakt os',
    primaryAction,
    secondaryAction,
    ...props
}) => {
    const id = useId(props.id);
    const defaultActions = React.useMemo(
        () =>
            createDefaultErrorActions({
                backLabel: errorBackText,
                contactLabel: errorContactText,
                contactHref: errorContactLink,
            }),
        [errorBackText, errorContactLink, errorContactText]
    );

    const resolvedPrimaryAction = primaryAction ?? defaultActions.primaryAction;
    const resolvedSecondaryAction = secondaryAction === null ? null : secondaryAction ?? defaultActions.secondaryAction;
    return (
        <div {...props} id={id}>
            <PageHeader theme="secondary" id={`${id}-pageheader`} title={errorHeader ? errorHeader : 'Der opstod en fejl'} />
            <Section>
                <div className="Box ">
                    <h4>{errorTitle}</h4>
                    <p>{errorDescription}</p>
                    {renderAction(resolvedPrimaryAction, PRIMARY_VARIANT)}
                    {resolvedSecondaryAction ? renderAction(resolvedSecondaryAction, SECONDARY_VARIANT) : null}
                </div>
            </Section>
        </div>
    );
};

export type { ErrorPageProps, ErrorPageAction, ErrorPageActionVariant };
export { ErrorPage, createDefaultErrorActions };
